import {Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {OrderService} from '../../shared/services/order.service';
import {MatDialog} from '@angular/material/dialog';
import {CartItem} from '../../shared/components/cart-item/cart-item';
import {CartService} from '../../shared/services/cart.service';
import {ContactOrderComponent} from '../contact-order/contact-order.component';
import {YesNoDialogComponent} from '../../yesno-dialog/yesno-dialog.component';
import {WaitModalComponent} from '../../wait-modal/wait-modal.component';
import {NotificationService} from '../../shared/services/notification.service';
import {User} from '../../shared/models/user';
import {SessionService} from '../../shared/services/session.service';
import {Subscription} from 'rxjs/internal/Subscription';
import {WINDOW} from '../../shared/services/window.service';
import {environment} from "../../../environments/environment";
import {BreakpointObserver} from "@angular/cdk/layout";
import {BREAKPOINTS} from "../../shared/constants/breakpoints";

@Component({
  selector: 'mbnm-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss']
})
export class OrderComponent implements OnInit, OnDestroy {
  orderId: string;
  order: any;
  error: any;
  cartItems: Array<CartItem>;
  currentUser: User;
  orderBeingEdited:string;
  userChangedSubscription: Subscription;
  cartChangedSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private orderService: OrderService,
    private cartService: CartService,
    private router: Router,
    private notificationService: NotificationService,
    public media: BreakpointObserver,
    public dialog: MatDialog,
    @Inject(WINDOW) private window: Window
  ) { }

  ngOnInit() {
    this.userChangedSubscription = SessionService.currentUserChanged.subscribe((currentUser:User) => {
      this.currentUser = currentUser;
      const params = this.route.snapshot.params;
      if (params['orderId']) {
        this.orderId = params['orderId'];
        this.getOrder();
      }
    });

    this.cartChangedSubscription = CartService.currentCartChanged.subscribe( (currentCart) => {
      this.orderBeingEdited = currentCart ? currentCart.orderConfirmationNumber : null;
    });
  }

  ngOnDestroy() {
    if (this.userChangedSubscription && !this.userChangedSubscription.closed) {
      this.userChangedSubscription.unsubscribe();
    }

    if (this.cartChangedSubscription && !this.cartChangedSubscription.closed) {
      this.cartChangedSubscription.unsubscribe();
    }
  }

  public getDescription(item: CartItem): string {
    let heading;
    if (item.description) {
      heading = item.description;
    } else if (item.primaryProduct === 'MAT') {
      heading = item.topMat.categoryName;
    } else if (item.primaryProduct === 'MIRROR') {
      heading = item.mirror.description;
    } else if (item.primaryProduct === 'FRAME') {
      heading = item.moulding.description;
    } else if (item.primaryProduct === 'BACKINGBOARD') {
      heading = item.backingBoard.description;
    } else if (item.primaryProduct === 'GLAZING') {
      heading = item.glazing.description;
    } else if (item.primaryProduct === 'SHOWBAG') {
      heading = 'Show Bags';
    } else if (item.primaryProduct === 'SAMPLE') {
      if (item.topMat) {
        heading = item.topMat.categoryName;
      } else if (item.mirror) {
        heading = item.mirror.description;
      } else if (item.moulding) {
        heading = item.moulding.description;
      } else if (item.backingBoard) {
        heading = item.backingBoard.description;
      } else if (item.glazing) {
        heading = item.glazing.description;
      } else if (item.bag) {
        heading = 'Show Bags';
      }
    }
    return heading;
  }

  public addToCart($event = null): void {
    this.addItemsToCart($event ? [$event.item] : [... this.cartItems.map(i => Object.assign({}, i))]);
  }

  private async addItemsToCart(itemsToAdd: Array<CartItem>, dialogRef = null) {
    if (dialogRef === null) {
      dialogRef = this.dialog.open(WaitModalComponent, {});

      dialogRef.disableClose = true;
      dialogRef.componentInstance.state = 'PROCESSING';

      dialogRef.componentInstance.titles = {
        PROCESSING: this.orderBeingEdited ? 'Adding to Order...' : 'Adding to Cart...',
        ERROR: this.orderBeingEdited ? 'Add to Order Error' : 'Add to Cart Error',
        DONE: this.orderBeingEdited ? 'Added to Order' : 'Added to Cart'
      };

      const itemStr = itemsToAdd.length > 1 ? 'items' : 'item';
      const itemStrPlusTense = itemsToAdd.length > 1 ? 'items have' : 'item has';
      dialogRef.componentInstance.text = {
        PROCESSING: this.orderBeingEdited ? 'Please wait while we add your previous order ' + itemStr + ' to current order.' : 'Please wait while we add your previous order  ' + itemStr + ' to cart.',
        ERROR: this.orderBeingEdited ? 'We encountered an unexpected error while adding to order.' : 'We encountered an unexpected error while adding to cart.',
        DONE: this.orderBeingEdited ? 'Your previous order ' + itemStrPlusTense + ' been added to your order.' : 'Your previous order ' + itemStrPlusTense + ' been added to cart.'
      };

      dialogRef.componentInstance.yesButton = {
        text: this.orderBeingEdited ? 'Review Order' : 'View Cart'
      };

      dialogRef.componentInstance.noButton = {
        text: 'Close Window'
      };

      dialogRef.afterClosed().subscribe( (result:boolean) => {
        if (result) {
          this.router.navigateByUrl(this.orderBeingEdited ? '/order' : '/cart');
        }
      });
    }

    try {
      await this.cartService.addItemsToCart(itemsToAdd);
      dialogRef.componentInstance.state = 'DONE';

      itemsToAdd.forEach( (item) => {
        const match = this.cartItems.filter( (cartItem) => {
          return cartItem.id === item.originalId;
        })[0];
        if (match) {
          match.rebought = true;
        }
      });
    } catch (resp: any) {
      const partTypeMap = {
        BB: 'Backing Board',
        FRAME: 'Moulding',
        GLAZING: 'Plexiglass',
        MAT: 'Matboard',
        SHOWBAG: 'Showbag'
      };
      dialogRef.componentInstance.state = 'ERROR';
      if (resp.error && resp.error.ErrorCode === 'INACTIVE_PARTS') {
        dialogRef.componentInstance.titles.ERROR = 'Inactive Parts Detected';
        if (itemsToAdd.length === 1) {
          dialogRef.componentInstance.text.ERROR = 'We can\'t add this item to your ' + (this.orderBeingEdited ? 'order' : 'cart') + ' due to the following inactive parts:<br /><br />';
        } else {
          dialogRef.componentInstance.text.ERROR = 'One or more of these items can\'t be re-ordered due to the following inactive parts:<br /><br />';
        }
        resp.error.ErrorData.forEach(p => {
          dialogRef.componentInstance.text.ERROR += '<div>' + partTypeMap[p.descriptor] + ': <strong>#' + p.code + "</strong></div>"
        })
        dialogRef.componentInstance.text.ERROR += '<br>Please contact customer service for help with alternatives.';
      }
    }

    /*
    this.cartService.addItemToCart(itemsToAdd.pop())
      .subscribe(
        cart => {
          if (itemsToAdd.length) {
            this.addItemsToCart(itemsToAdd, dialogRef);
          } else {
            dialogRef.componentInstance.state = 'DONE';
          }
        }
      );
      */
  }

  public print(): void {
    this.window.open(environment.baseUrls().api + 'user/order/' + this.order.ConfirmationCode + '/invoice');
  }

  public showContactForm($event): void {
    if ($event) $event.currentTarget.blur();
    const dialogRef = this.dialog.open(ContactOrderComponent, {});
    dialogRef.componentInstance.order = this.order;
  }

  public async changeOrder($event) {
    if ($event) $event.currentTarget.blur();
    if (this.orderBeingEdited) {
      let dialogRef = this.dialog.open(YesNoDialogComponent, {});

      let instance = dialogRef.componentInstance;
      instance.data = {
        title: 'Another Order Being Edited',
        message: '<p>Another order has already been prepared for changes.  Discard any changes made to that order before attempting to change this order.</p>',
        yesButtonText: 'OK'
      };

    } else {
      if (this.order.Status !== 'NEW' && this.order.Status !== 'READY') {
        let message;
        switch (this.order.Status) {
          case 'PROCESSING':
            message = 'We\'ve already started working on your order! If you really need to change your order, ' +
              'contact us immediately and we\'ll do what we can to help!';
            break;
          case 'SHIPPED':
          case 'DELIVERED':
            message = 'Your order has already shipped and can\'t be changed! If you are looking to track your shipment,' +
              ' click the "track" button.';
            break;
          case 'CANCELLED':
            message = 'Your order was cancelled. You cannot change a cancelled order.';
            break;
          case 'HOLD':
          case 'PRODUCTION-HOLD':
            message = 'Your order is on hold. You are either already in the process of changing it or we\'ve ' +
              'placed it on hold for another reason.';
            break;
          default:
            message = 'Your order is in an invalid state: ' + this.order.Status + '.  Please contact us for help.';
            break;
        }
        this.notificationService.createNotification(message, 'warn');
      } else {

        let dialogRef = this.dialog.open(WaitModalComponent, {});

        let instance = dialogRef.componentInstance;
        dialogRef.disableClose = true;
        instance.state = 'PROCESSING';

        instance.titles = {
          PROCESSING: 'Preparing Order for Changes',
          ERROR: 'Error Preparing Order',
          DONE: 'Order Ready for Changes'
        };

        instance.text = {
          PROCESSING: 'Please wait while we prepare your order for changes.',
          ERROR: 'We encountered an unexpected error while preparing your order to be changed.',
          DONE: ''
        };

        instance.yesButton = {
          text: 'Continue',
        };

        instance.noButton = {
          text: 'Cancel'
        };

        dialogRef.afterClosed().subscribe(async (result: boolean) => {
          if (result) {
            this.router.navigateByUrl('/order');
          } else {
            this.setOrderData(await this.orderService.cancelEditOrder(this.order.ConfirmationCode));
            // need to get the cart now that it's been replaced with an existing order
            await this.cartService.getCart();
          }
        });

        this.setOrderData(await this.orderService.editOrder(this.orderId));

        // need to get the cart now that it's been replaced with an existing order
        await this.cartService.getCart();

        instance.text.DONE = '<p>Order #' + this.order.ConfirmationCode + ' has been <strong>placed on hold</strong> while you make your changes.</p>' +
          '<p>If you do not wish to make any changes, click cancel to undo this and release your order from hold.</p>';
        instance.state = 'DONE';
      }
    }
  }

  public cancelOrder($event): void {
    $event.currentTarget.blur();
    const dialogRef = this.dialog.open(YesNoDialogComponent, {});
    dialogRef.componentInstance.data = {
      title: 'Cancel Order?',
      message: '<p>You can still change your order, are you sure you want to cancel?</p><p>This action cannot be undone.</p>',
      yesButtonText: 'Yes, Cancel Order',
      noButtonText: 'Change Order',
      closeMethod: 'x',
      primaryButtonColor: 'warn'
    };

    dialogRef.afterClosed().subscribe(async confirmCancel => {
      if (confirmCancel === true) {
        this.setOrderData(await this.orderService.cancelOrder(this.order.ConfirmationCode));
      } else if (confirmCancel === false) {
        this.changeOrder(null);
      }
    });
  }

  public trackPackage($event): void {
    $event.currentTarget.blur();
    const order = this.order;
    if ((order.Status !== 'SHIPPED' && order.Status !== 'DELIVERED') || (!order.TrackingNumber && !order.TrackingLink)) {
      this.showTrackNotif(order);
    } else {
        this.window.open(order.TrackingLink);
    }
  }

  private showTrackNotif(order): void {
    let message;
    let type;
    switch (order.Status) {
      case 'PROCESSING':
        message = 'We\'ve working on your order, and will ship it as soon as it\'s done. When it ships,' +
                  ' we will email you and enable this button so you can track the order.';
        type = 'info';
        break;
      case 'NEW':
      case 'READY':
        message = 'We will start working on your order soon! We usually give you some time to change ' +
                  'your order before we start working on it.';
        type = 'info';
        break;
      case 'CANCELLED':
        message = 'Your order was cancelled. You cannot track a cancelled order.';
        type = 'warn';
        break;
      case 'HOLD':
      case 'PRODUCTION-HOLD':
      case 'HOLD-REQUEST':
      case 'CANCEL-REQUEST':
        message = 'Your order is on hold. You cannot track this order until it ships.';
        type = 'info';
        break;
      case 'TEST':
        message = 'This is a test order and therefore will not actually be processed.';
        type = 'info';
        break;
      case 'SHIPPED':
      case 'DELIVERED':
        if (!order.TrackingLink) {
          const subMessage = order.type === 'sample' ? 'Samples are usually shipped this way.' :
              'If you feel your order should have a tracking number, feel free to contact us for further assistance.';
          message = `
          <p>
            Your order was shipped using a method that does not have tracking such as USPS.
          </p>
          <p>${subMessage}</p>
          `;
          type = 'warn';
        }
        break;
    }
    if (message && type) {
      this.notificationService.createNotification(message, type);
    }
  }

  private async getOrder() {
    try {
      const order = await this.orderService.getOrderById(this.orderId);
      this.setOrderData(order);
      const url = this.route.snapshot.url;
      switch (url[url.length - 1].path) {
        case 'contact': {
          this.showContactForm(null);
        } break;
        case 're-order': {
          this.addToCart(null);
        } break;
      }
    } catch (error: any) {
      this.error = error;
    }
  }

  private setOrderData(order:any):void {
    this.order = order;

    order.Cart.orderlines.forEach( orderline => {
      this.cartService.setCartItemExtras(orderline);
    });

    this.cartItems = order.Cart.orderlines;
  }

  protected readonly BREAKPOINTS = BREAKPOINTS;
}
